import React, {useCallback, useEffect, useState} from "react";
import {useAuth} from "../auth";
import {apiFetch, ApiError} from "../api/client";
import "./MerchantActions.css";

type Audience = "merchant" | "trader";
interface Policy {audience: Audience; usdtMinimum: string | null; version: number}

export function WithdrawalMinimum({audience, editable = false}: {audience: Audience; editable?: boolean}): JSX.Element {
  const title = audience === "merchant" ? "Минимум вывода мерчантов" : "Минимум вывода трейдеров";
  const {token} = useAuth();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const load = useCallback(async (): Promise<void> => {
    try {
      const value = await apiFetch<Policy>(`/withdrawal-policy?audience=${audience}`, token);
      setPolicy(value); setEnabled(value.usdtMinimum !== null); setAmount(value.usdtMinimum ?? ""); setMessage("");
    } catch { setMessage("Не удалось получить минимум USDT. При создании вывода сервер проверит действующий порог."); }
  }, [token, audience]);
  useEffect(() => { void load(); }, [load]);
  const save = async (): Promise<void> => {
    if (!policy || busy) return;
    if (enabled && (!/^(0|[1-9]\d{0,23})(\.\d{1,6})?$/.test(amount) || !/[1-9]/.test(amount))) {
      setMessage("Введите положительную сумму USDT, до 6 знаков после точки."); return;
    }
    setBusy(true); setMessage("");
    try {
      const value = await apiFetch<Policy>("/withdrawal-policy", token, {method: "PUT", body: JSON.stringify({audience, usdtMinimum: enabled ? amount : null, version: policy.version})});
      setPolicy(value); setMessage("Настройка сохранена.");
    } catch (error) {
      setMessage(error instanceof ApiError && error.status === 409
        ? "Настройку уже изменили. Обновите её перед сохранением."
        : "Не удалось подтвердить сохранение. Обновите настройку, чтобы проверить результат.");
    } finally { setBusy(false); }
  };
  return <section className="merchant-section-card withdrawal-minimum" aria-label={title}>
    {editable && <h2>{title}</h2>}
    {policy && <p>{policy.usdtMinimum === null ? "Минимум вывода USDT не установлен." : `Минимальная сумма заявки USDT: ${policy.usdtMinimum} USDT.`}</p>}
    {editable && <>
      <p>{audience === "merchant"
        ? "Для новых выводов мерчантов в TRC20 и BEP20. Минимум трейдеров настраивается отдельно на странице «Трейдеры»."
        : "Для новых выводов USDT команд трейдеров, включая вывод всей доступной суммы. Не влияет на выводы мерчантов."}</p>
      <label className="withdrawal-minimum-toggle"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} disabled={busy || !policy}/> Включить минимум</label>
      {enabled && <label className="withdrawal-minimum-amount">Минимум, USDT<input type="text" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} disabled={busy}/></label>}
      <button type="button" className="btn btn-primary" disabled={busy || !policy} onClick={() => void save()}>{busy ? "Сохранение…" : "Сохранить минимум"}</button>
    </>}
    {message && <p role="status">{message}</p>}
    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void load()}>Обновить минимум</button>
  </section>;
}
